import {
  officeErrorToLogObject,
  wordSyncWithLog,
} from "../utils/officeJsDebugLog";

/**
 * Word.js + Office common: bracket placeholders and full-document export for save-back.
 */

const LOG_PREFIX = "[gameRuleWord]";

async function ensureOfficeReady(): Promise<void> {
  if (typeof Office === "undefined") {
    throw new Error("Office.js is not loaded");
  }
  await Office.onReady();
}

export const insertBracketPlaceholder = async (
  fieldKey: string,
): Promise<void> => {
  const key = fieldKey.trim().toUpperCase();
  if (!/^[A-Z0-9_]+$/.test(key)) {
    throw new Error("Field key must use only letters, digits, and underscores");
  }
  const token = `[${key}]`;
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  await Word.run(async (context) => {
    const range = context.document.getSelection();
    range.insertText(token, Word.InsertLocation.replace);
    await context.sync();
  });
};

/** Remove every exact `[KEY]` occurrence from the main story (same token shape as insertBracketPlaceholder). */
export const removeBracketPlaceholdersForKey = async (
  fieldKey: string,
): Promise<void> => {
  const key = fieldKey.trim().toUpperCase();
  if (!/^[A-Z0-9_]+$/.test(key)) {
    return;
  }
  const token = `[${key}]`;
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  await Word.run(async (context) => {
    const body = context.document.body;
    const results = body.search(token, {
      matchCase: true,
      matchWholeWord: false,
    });
    context.load(results, "items");
    await context.sync();
    for (let i = results.items.length - 1; i >= 0; i -= 1) {
      results.items[i].delete();
    }
    await context.sync();
  });
};

/**
 * Reads Word's dirty/saved flag ([Api set: WordApi 1.1]). Returns null on error (unsupported host, etc.).
 */
export const readDocumentSavedIfSupported = async (): Promise<
  boolean | null
> => {
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    return null;
  }
  try {
    return await Word.run(async (context) => {
      const doc = context.document;
      doc.load("saved");
      await context.sync();
      return doc.saved;
    });
  } catch {
    return null;
  }
};

/** Normalize Word plain-text line endings without collapsing spaces or internal blank lines. */
export function normalizeWordPlainText(raw: string): string {
  return raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

/** Strip Word paragraph-end marks from a single paragraph's text. */
export function stripParagraphEndMark(text: string): string {
  return text.replace(/[\r\n\u0007\u000b\u000c]+$/g, "");
}

/** Format one paragraph for plain-text preview, including numbered/lettered list markers. */
export function formatParagraphPreviewLine(
  rawText: string,
  isListItem: boolean,
  listString?: string | null,
): string {
  const text = stripParagraphEndMark(normalizeWordPlainText(rawText));
  const marker = (listString ?? "").trimEnd();
  if (isListItem && marker) {
    return text ? `${marker}\t${text}` : marker;
  }
  return text;
}

export function buildPlainTextPreviewFromParagraphs(
  paragraphs: ReadonlyArray<{
    readonly rawText: string;
    readonly isListItem: boolean;
    readonly listString?: string | null;
  }>,
): string {
  return paragraphs
    .map((p) =>
      formatParagraphPreviewLine(p.rawText, p.isListItem, p.listString),
    )
    .join("\n")
    .trimEnd();
}

async function readSelectionPlainTextFromContext(
  context: Word.RequestContext,
): Promise<string> {
  const selection = context.document.getSelection();
  const paragraphs = selection.paragraphs;
  selection.load("text");
  paragraphs.load("items");
  await context.sync();

  const items = paragraphs.items;
  if (items.length === 0) {
    return normalizeWordPlainText(selection.text ?? "").trimEnd();
  }

  const prepared = items.map((paragraph) => {
    paragraph.load(["isListItem"]);
    paragraph.listItemOrNullObject.load("listString");
    const intersectRange = paragraph
      .getRange()
      .intersectWithOrNullObject(selection);
    intersectRange.load(["text", "isNullObject"]);
    return { paragraph, intersectRange };
  });
  await context.sync();

  const active = prepared.filter((entry) => !entry.intersectRange.isNullObject);
  if (active.length === 0) {
    return normalizeWordPlainText(selection.text ?? "").trimEnd();
  }

  const compared = active.map(({ paragraph, intersectRange }) => {
    const paraStart = paragraph.getRange(Word.RangeLocation.start);
    const intersectStart = intersectRange.getRange(Word.RangeLocation.start);
    const startRelation = paraStart.compareLocationWith(intersectStart);
    return { paragraph, intersectRange, startRelation };
  });
  await context.sync();

  const segments = compared.map(
    ({ paragraph, intersectRange, startRelation }) => {
      const includesParaStart =
        startRelation.value === Word.LocationRelation.equal;
      return {
        rawText: intersectRange.text ?? "",
        isListItem: includesParaStart && paragraph.isListItem,
        listString:
          includesParaStart && !paragraph.listItemOrNullObject.isNullObject
            ? paragraph.listItemOrNullObject.listString
            : null,
      };
    },
  );

  return buildPlainTextPreviewFromParagraphs(segments);
}

/** Full untruncated plain text for the current Word selection. */
export const getSelectionPlainText = async (): Promise<string> => {
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  return Word.run(async (context) => readSelectionPlainTextFromContext(context));
};

export const getSelectionTextPreview = async (
  maxChars: number,
): Promise<string> => {
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  return Word.run(async (context) => {
    const text = await readSelectionPlainTextFromContext(context);
    if (!text.trim()) {
      return "";
    }
    return text.length > maxChars ? `${text.slice(0, maxChars)}…` : text;
  });
};

export type TagKbFieldAtSelectionResult = {
  readonly selectedText: string;
  readonly docAnchor: string;
};

/** Remove content controls tagged with a KB field anchor (kebab-case). */
export const removeKbFieldContentControlByTag = async (
  docAnchor: string,
): Promise<void> => {
  const tag = docAnchor.trim();
  if (!tag) {
    return;
  }
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  await Word.run(async (context) => {
    const ccs = context.document.contentControls.getByTag(tag);
    ccs.load("items");
    await wordSyncWithLog(context, LOG_PREFIX, "removeKbFieldCC:load", {
      tag,
    });
    for (let i = ccs.items.length - 1; i >= 0; i -= 1) {
      ccs.items[i].delete(true);
    }
    await wordSyncWithLog(context, LOG_PREFIX, "removeKbFieldCC:delete", {
      tag,
    });
  });
};

/**
 * Wrap the current Word selection in a rich-text content control tagged with `docAnchor`.
 * Preserves the selected text inside the control.
 */
export const tagKbFieldAtSelection = async (
  docAnchor: string,
): Promise<TagKbFieldAtSelectionResult> => {
  const tag = docAnchor.trim();
  if (!tag) {
    throw new Error("Document anchor is required.");
  }
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }

  const selectedText = (await getSelectionPlainText()).trim();
  if (!selectedText) {
    throw new Error("Select content in the Word document before tagging.");
  }

  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.load("text");
    await context.sync();
    const rangeText = (selection.text ?? "").trim();
    if (!rangeText) {
      throw new Error("Select content in the Word document before tagging.");
    }

    const overlapping = selection.contentControls;
    overlapping.load("items");
    await wordSyncWithLog(context, LOG_PREFIX, "tagKbField:overlap load", {
      tag,
    });
    if (overlapping.items.length > 0) {
      throw new Error(
        "Selection overlaps an existing content control. Select plain text instead.",
      );
    }

    const cc = selection.insertContentControl(Word.ContentControlType.richText);
    cc.tag = tag;
    cc.title = tag;
    cc.cannotDelete = false;
    cc.cannotEdit = false;
    await wordSyncWithLog(context, LOG_PREFIX, "tagKbField:sync", { tag });
  });

  return { selectedText, docAnchor: tag };
};

/**
 * Export the current document as OOXML (compressed package) bytes via Office.context.document.getFileAsync.
 */

const PLACEHOLDER_HINTS = [
  "click or tap here",
  "click here to enter",
  "tap here to enter",
  "choose an item",
  "pick a date",
  "enter text",
  "type here",
  "select date",
  "select an item",
];

/** True when the control body and/or Word placeholder hint look unfilled. */
const isLikelyPlaceholderOrEmpty = (
  bodyText: string,
  placeholderText?: string | null,
): boolean => {
  const body = bodyText.replace(/\s+/g, " ").trim();
  const ph = (placeholderText ?? "").replace(/\s+/g, " ").trim();
  const combined = `${body} ${ph}`.trim().toLowerCase();
  if (!combined.replace(/\s/g, "")) {
    return true;
  }
  if (PLACEHOLDER_HINTS.some((h) => combined.includes(h))) {
    return true;
  }
  // Single paragraph mark / odd whitespace: treat as empty for tagging purposes
  if (body.length <= 1 && !/[A-Za-z0-9]/.test(body)) {
    return true;
  }
  return false;
};

const normalizeTitleToFieldKey = (
  title: string | undefined | null,
): string | null => {
  const raw = (title ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/[^A-Z0-9_]/g, "");
  return raw && /^[A-Z0-9_]+$/.test(raw) ? raw : null;
};

/**
 * Replace every literal `[KEY]` in the main story with the given display text (longest keys first).
 * Run after {@link applyContentControlValuesByTag} so any `[KEY]` tokens the content-control path
 * missed still receive values. Template authoring does not call this unless you add it explicitly.
 */
export const replaceBracketPlaceholdersWithTaggedValues = async (
  displayValuesByUpperKey: Record<string, string>,
): Promise<void> => {
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  const entries = Object.entries(displayValuesByUpperKey)
    .map(([k, v]) => [k.trim().toUpperCase(), v ?? ""] as const)
    .filter(([k]) => /^[A-Z0-9_]+$/.test(k));
  const sorted = [...entries].sort((a, b) => b[0].length - a[0].length);

  console.info(LOG_PREFIX, "replaceBracketPlaceholdersWithTaggedValues start", {
    keyCount: sorted.length,
    keysSample: sorted.map(([k]) => k).slice(0, 24),
  });
  try {
    await Word.run(async (context) => {
      const body = context.document.body;
      for (const [key, val] of sorted) {
        const token = `[${key}]`;
        let guard = 0;
        while (guard < 1000) {
          guard += 1;
          const results = body.search(token, {
            matchCase: true,
            matchWholeWord: false,
          });
          context.load(results, "items");
          await wordSyncWithLog(
            context,
            LOG_PREFIX,
            "replaceBracket:search load",
            { key, guard },
          );
          if (results.items.length === 0) {
            break;
          }
          const range = results.items[0];
          range.insertText(val, Word.InsertLocation.replace);
          await wordSyncWithLog(
            context,
            LOG_PREFIX,
            "replaceBracket:after insertText",
            { key, guard },
          );
        }
      }
    });
    console.info(
      LOG_PREFIX,
      "replaceBracketPlaceholdersWithTaggedValues complete",
    );
  } catch (e) {
    console.error(
      LOG_PREFIX,
      "replaceBracketPlaceholdersWithTaggedValues failed",
      officeErrorToLogObject(e),
    );
    throw e;
  }
};

export type MigrateBracketOptions = {
  readonly signal?: AbortSignal;
};

const MAX_MIGRATE_BRACKET_OCCURRENCES_PER_KEY = 64;

async function bracketRangeIsInsideSameTaggedPlaceholder(
  context: Word.RequestContext,
  range: Word.Range,
  upperKey: string,
): Promise<boolean> {
  try {
    const parent = range.parentContentControl;
    parent.load(["tag", "isNullObject"]);
    await context.sync();
    if (parent.isNullObject) {
      return false;
    }
    const pTag = (parent.tag ?? "").trim().toUpperCase();
    return pTag === upperKey;
  } catch {
    return false;
  }
}

/**
 * Wrap each literal `[KEY]` in a **rich-text** content control tagged with KEY (longest keys first).
 *
 * Uses **richText** (not plainText) because `Range.insertContentControl(plainText)` often throws
 * RichApi **GeneralException** in tables, complex paragraphs, and other story regions on desktop Word.
 *
 * Each match runs in its own `Word.run` to keep batches small and avoid overlapping strict-mode pipelines.
 */
export const migrateBracketPlaceholdersToContentControls = async (
  fieldKeys: readonly string[],
  options?: MigrateBracketOptions,
): Promise<void> => {
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  const signal = options?.signal;
  const throwIfAborted = (): void => {
    if (signal?.aborted) {
      throw new DOMException("Bracket migration aborted", "AbortError");
    }
  };

  const normalized = fieldKeys
    .map((k) => k.trim().toUpperCase())
    .filter((k) => /^[A-Z0-9_]+$/.test(k));
  const sorted = [...new Set(normalized)].sort((a, b) => b.length - a.length);

  console.info(
    LOG_PREFIX,
    "migrateBracketPlaceholdersToContentControls start",
    {
      keyCount: sorted.length,
      keysSample: sorted.slice(0, 24),
    },
  );
  try {
    for (const key of sorted) {
      throwIfAborted();
      const token = `[${key}]`;
      let wrapped = 0;
      for (
        let guard = 0;
        guard < MAX_MIGRATE_BRACKET_OCCURRENCES_PER_KEY;
        guard += 1
      ) {
        throwIfAborted();
        if (guard > 0 && guard % 16 === 0) {
          console.info(LOG_PREFIX, "migrateBracket: still running", {
            key,
            guard,
          });
        }
        const outcome = await Word.run(async (context) => {
          const body = context.document.body;
          const results = body.search(token, {
            matchCase: true,
            matchWholeWord: false,
          });
          context.load(results, "items");
          await wordSyncWithLog(
            context,
            LOG_PREFIX,
            "migrateBracket:search load",
            { key, guard },
          );
          if (results.items.length === 0) {
            return "none" as const;
          }
          const range = results.items[0];
          if (
            await bracketRangeIsInsideSameTaggedPlaceholder(context, range, key)
          ) {
            console.info(
              LOG_PREFIX,
              "migrateBracket: stop — match is inside tagged placeholder",
              {
                key,
                guard,
              },
            );
            return "none" as const;
          }
          const overlapping = range.contentControls;
          overlapping.load("items");
          await wordSyncWithLog(
            context,
            LOG_PREFIX,
            "migrateBracket:overlap load",
            { key, guard },
          );
          if (overlapping.items.length > 0) {
            console.warn(
              LOG_PREFIX,
              "migrateBracket: token range overlaps content control(s); stop this key",
              {
                key,
                overlappingCount: overlapping.items.length,
              },
            );
            return "blocked" as const;
          }
          const cc = range.insertContentControl(
            Word.ContentControlType.richText,
          );
          cc.tag = key;
          cc.insertText("", Word.InsertLocation.replace);
          await wordSyncWithLog(
            context,
            LOG_PREFIX,
            "migrateBracket:after CC",
            { key, guard },
          );
          return "wrapped" as const;
        });
        if (outcome === "none") {
          break;
        }
        if (outcome === "blocked") {
          break;
        }
        wrapped += 1;
      }
      if (wrapped > 0) {
        console.info(LOG_PREFIX, "migrateBracket key done", {
          key,
          wrappedOccurrences: wrapped,
        });
      }
    }
    console.info(
      LOG_PREFIX,
      "migrateBracketPlaceholdersToContentControls complete",
    );
  } catch (e) {
    console.error(
      LOG_PREFIX,
      "migrateBracketPlaceholdersToContentControls failed",
      officeErrorToLogObject(e),
    );
    throw e;
  }
};

/**
 * Word templates often use built-in rich-text controls showing "Click or tap here to enter text"
 * with no `tag`. Map those controls to field keys so {@link applyContentControlValuesByTag} can
 * drive them from the form (document order + `fields_json` order).
 *
 * 1) Content controls whose `title` normalizes to a known key get that `tag`.
 * 2) Remaining keys are paired in order with placeholder-like or empty plain/rich text controls.
 */
export const assignTagsToPlaceholderContentControls = async (
  fieldKeysInOrder: readonly string[],
  fieldLabelHints?: ReadonlyArray<{
    readonly label: string;
    readonly key: string;
  }>,
): Promise<void> => {
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }

  const keyOrder: string[] = [];
  const seen = new Set<string>();
  for (const raw of fieldKeysInOrder) {
    const k = raw.trim().toUpperCase();
    if (!/^[A-Z0-9_]+$/.test(k) || seen.has(k)) {
      continue;
    }
    seen.add(k);
    keyOrder.push(k);
  }
  if (!keyOrder.length) {
    return;
  }

  console.info(LOG_PREFIX, "assignTagsToPlaceholderContentControls start", {
    keyOrderLength: keyOrder.length,
    keyOrderSample: keyOrder.slice(0, 24),
    hasLabelHints: Boolean(fieldLabelHints?.length),
  });

  const isBindableContentControl = (cc: Word.ContentControl): boolean => {
    const typeStr = String(cc.type);
    return !(
      typeStr.includes("CheckBox") ||
      typeStr.includes("DropDownList") ||
      typeStr.includes("ComboBox") ||
      typeStr.includes("BuildingBlockGallery") ||
      typeStr.includes("DatePicker")
    );
  };

  try {
    await Word.run(async (context) => {
      const coll = context.document.contentControls;
      coll.load("items");
      await wordSyncWithLog(
        context,
        LOG_PREFIX,
        "assignTags:load contentControls",
        {},
      );
      const items = coll.items;
      for (let i = 0; i < items.length; i += 1) {
        items[i].load(["tag", "title", "text", "type", "placeholderText"]);
      }
      await wordSyncWithLog(
        context,
        LOG_PREFIX,
        "assignTags:load each CC props",
        { ccCount: items.length },
      );

      const tagged = new Set<string>();
      for (const cc of items) {
        const t = (cc.tag ?? "").trim().toUpperCase();
        if (/^[A-Z0-9_]+$/.test(t)) {
          tagged.add(t);
        }
      }

      const keySet = new Set(keyOrder);

      const titleToKey = new Map<string, string>();
      if (fieldLabelHints) {
        for (const { label, key } of fieldLabelHints) {
          const k = key.trim().toUpperCase();
          if (!/^[A-Z0-9_]+$/.test(k)) {
            continue;
          }
          const t = label.trim().toLowerCase();
          if (t) {
            titleToKey.set(t, k);
          }
        }
      }

      for (const cc of items) {
        if (!isBindableContentControl(cc)) {
          continue;
        }
        const existing = (cc.tag ?? "").trim().toUpperCase();
        if (/^[A-Z0-9_]+$/.test(existing)) {
          continue;
        }
        let fromTitle = normalizeTitleToFieldKey(cc.title);
        if (!fromTitle && fieldLabelHints) {
          const raw = (cc.title ?? "").trim().toLowerCase();
          const byLabel = titleToKey.get(raw);
          if (byLabel) {
            fromTitle = byLabel;
          }
        }
        if (fromTitle && keySet.has(fromTitle)) {
          cc.tag = fromTitle;
          tagged.add(fromTitle);
        }
      }
      await wordSyncWithLog(
        context,
        LOG_PREFIX,
        "assignTags:after title mapping",
        {
          taggedCount: tagged.size,
        },
      );

      const keysStill = keyOrder.filter((k) => !tagged.has(k));
      let ki = 0;
      for (const cc of items) {
        if (ki >= keysStill.length) {
          break;
        }
        if (!isBindableContentControl(cc)) {
          continue;
        }
        const existing = (cc.tag ?? "").trim().toUpperCase();
        if (/^[A-Z0-9_]+$/.test(existing)) {
          continue;
        }
        const bodyText = (cc.text ?? "").trim();
        const phText =
          typeof (cc as Word.ContentControl & { placeholderText?: string })
            .placeholderText === "string"
            ? (cc as Word.ContentControl & { placeholderText?: string })
                .placeholderText
            : null;
        if (!isLikelyPlaceholderOrEmpty(bodyText, phText)) {
          continue;
        }
        const nextKey = keysStill[ki];
        cc.tag = nextKey;
        tagged.add(nextKey);
        ki += 1;
      }
      await wordSyncWithLog(context, LOG_PREFIX, "assignTags:final", {
        keysStillCount: keysStill.length,
        pairedCount: ki,
      });
    });
    console.info(LOG_PREFIX, "assignTagsToPlaceholderContentControls complete");
  } catch (e) {
    console.error(
      LOG_PREFIX,
      "assignTagsToPlaceholderContentControls failed",
      officeErrorToLogObject(e),
    );
    throw e;
  }
};

/** Types we can fill with {@link setContentControlText}; others need different APIs. */
const isRangeTextSubstitutableContentControl = (
  cc: Word.ContentControl,
): boolean => {
  const typeStr = String(cc.type);
  return !(
    typeStr.includes("CheckBox") ||
    typeStr.includes("DropDownList") ||
    typeStr.includes("ComboBox") ||
    typeStr.includes("BuildingBlockGallery") ||
    typeStr.includes("DatePicker") ||
    typeStr.includes("Picture")
  );
};

const setContentControlText = (cc: Word.ContentControl, text: string): void => {
  cc.insertText(text, Word.InsertLocation.replace);
};

/**
 * Push current form values into all content controls that share each field's tag.
 *
 * Queuing many `getRange` / `insertText` calls on different controls in one `sync()` often throws
 * RichApi **GeneralException** at `ContentControl._OnAccess` (stale proxies). We **re-query by tag
 * and `sync` after each control**.
 */
export const applyContentControlValuesByTag = async (
  valuesByKey: Record<string, string>,
): Promise<void> => {
  await ensureOfficeReady();
  if (typeof Word === "undefined") {
    throw new Error("Word.js API is not available in this host");
  }
  const tagKeys = Object.keys(valuesByKey)
    .map((k) => k.trim().toUpperCase())
    .filter((k) => /^[A-Z0-9_]+$/.test(k));
  console.info(LOG_PREFIX, "applyContentControlValuesByTag start", {
    tagCount: tagKeys.length,
    tagsSample: tagKeys.slice(0, 24),
  });
  try {
    await Word.run(async (context) => {
      for (const [rawKey, rawVal] of Object.entries(valuesByKey)) {
        const tag = rawKey.trim().toUpperCase();
        if (!/^[A-Z0-9_]+$/.test(tag)) {
          continue;
        }
        const text = rawVal ?? "";
        let index = 0;
        let guard = 0;
        while (guard < 200) {
          guard += 1;
          const ccs = context.document.contentControls.getByTag(tag);
          ccs.load("items");
          await wordSyncWithLog(
            context,
            LOG_PREFIX,
            "applyValuesByTag:load CCs",
            { tag, index },
          );
          if (ccs.items.length === 0) {
            if (index === 0 && import.meta.env.DEV) {
              console.warn(
                `[PrizeLogic] No Word content control with tag "${tag}"; form value will not appear in the document.`,
              );
            }
            break;
          }
          if (index >= ccs.items.length) {
            break;
          }
          const cc = ccs.items[index];
          cc.load("type");
          await wordSyncWithLog(
            context,
            LOG_PREFIX,
            "applyValuesByTag:load type",
            { tag, index },
          );
          if (!isRangeTextSubstitutableContentControl(cc)) {
            if (import.meta.env.DEV) {
              console.warn(
                LOG_PREFIX,
                "applyValuesByTag: skip non-text content control",
                {
                  tag,
                  index,
                  type: String(cc.type),
                },
              );
            }
            index += 1;
            continue;
          }
          setContentControlText(cc, text);
          await wordSyncWithLog(
            context,
            LOG_PREFIX,
            "applyValuesByTag:after setText",
            { tag, index },
          );
          index += 1;
        }
      }
    });
    console.info(LOG_PREFIX, "applyContentControlValuesByTag complete");
  } catch (e) {
    console.error(
      LOG_PREFIX,
      "applyContentControlValuesByTag failed",
      officeErrorToLogObject(e),
    );
    throw e;
  }
};

export const exportCurrentDocumentAsUint8Array =
  async (): Promise<Uint8Array> => {
    await ensureOfficeReady();
    if (
      typeof Office === "undefined" ||
      !Office.context?.document?.getFileAsync
    ) {
      throw new Error("Document export is not supported in this host");
    }
    console.info(
      LOG_PREFIX,
      "exportCurrentDocumentAsUint8Array: getFileAsync …",
    );
    return new Promise((resolve, reject) => {
      Office.context.document.getFileAsync(
        Office.FileType.Compressed,
        { sliceSize: 4194304 },
        (result: Office.AsyncResult<Office.File>) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            const err = result.error;
            console.error(LOG_PREFIX, "export getFileAsync failed", {
              code: err?.code,
              name: err?.name,
              message: err?.message,
            });
            reject(new Error(err?.message ?? "getFileAsync failed"));
            return;
          }
          const file = result.value;
          const sliceCount = file.sliceCount;
          const chunks: Uint8Array[] = [];
          let index = 0;
          const readSlice = (): void => {
            file.getSliceAsync(index, (sliceResult) => {
              if (sliceResult.status !== Office.AsyncResultStatus.Succeeded) {
                void file.closeAsync();
                const se = sliceResult.error;
                console.error(LOG_PREFIX, "export getSliceAsync failed", {
                  sliceIndex: index,
                  code: se?.code,
                  name: se?.name,
                  message: se?.message,
                });
                reject(new Error(se?.message ?? "getSliceAsync failed"));
                return;
              }
              const raw = sliceResult.value.data;
              const bytes =
                raw instanceof ArrayBuffer
                  ? new Uint8Array(raw)
                  : Uint8Array.from(raw as ArrayLike<number>);
              chunks.push(bytes);
              index += 1;
              if (index < sliceCount) {
                readSlice();
              } else {
                void file.closeAsync();
                const total = chunks.reduce((acc, c) => acc + c.length, 0);
                const out = new Uint8Array(total);
                let offset = 0;
                for (const c of chunks) {
                  out.set(c, offset);
                  offset += c.length;
                }
                console.info(
                  LOG_PREFIX,
                  "exportCurrentDocumentAsUint8Array ok",
                  {
                    byteLength: total,
                    sliceCount,
                  },
                );
                resolve(out);
              }
            });
          };
          readSlice();
        },
      );
    });
  };
